import React, { useEffect, useState } from 'react';

import { timingBridgeAPI } from '../../../lib/api';
import { HealthLight, Metric } from '../climbBridge/BridgeHealth';
import { FlagPill, RunningOrder } from './shared';
import ChampionshipPanel from './ChampionshipPanel';
import PhotosPanel from './PhotosPanel';
import BroadcastLinks from './BroadcastLinks';
import PlaybackTransport from '../climbBridge/PlaybackTransport';

const STREAM_VIEWS = ['tower', 'session', 'driver', 'battle', 'events', 'championship', 'legacy'];

/**
 * Screen 2 — Live.
 *
 * Everything the terminal tool could not do once it was running: pick the car
 * for the driver card, pick (or let it pick) the battle, filter the tower to a
 * class, freeze the graphics, move to another session — all without a restart.
 *
 * The payload viewer at the bottom shows the exact object a composition is
 * given, which settles "bridge problem or graphics problem" at a glance.
 */
export default function LivePanel({ summary, showId, onChanged }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState([]);
  const [switchTo, setSwitchTo] = useState('');
  const [view, setView] = useState('');

  const health = summary?.health;
  const upstream = health?.upstream || {};
  const streams = health?.publish?.streams || [];
  const pushesPerMinute = streams.reduce((sum, s) => sum + (s.pushesLastMinute || 0), 0);
  const publishFailures = streams.filter(s => s.configured && s.consecutiveFailures > 0);

  useEffect(() => {
    if (!showId) return undefined;
    let cancelled = false;
    const load = () => timingBridgeAPI.state(showId)
      .then(res => { if (!cancelled) setState(res.data); })
      .catch(() => {});
    load();
    const timer = setInterval(load, 1000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [showId]);

  async function act(name, fn) {
    setBusy(name);
    setError('');
    try {
      await fn();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Action failed');
    } finally {
      setBusy('');
    }
  }

  /** Tick two cars and they become the battle; untick to start again. */
  function toggleBattle(no) {
    const next = pending.includes(no) ? pending.filter(n => n !== no) : [...pending, no].slice(-2);
    setPending(next);
    if (next.length === 2) {
      act('battle', () => timingBridgeAPI.setBattle(showId, next)).then(() => setPending([]));
    }
  }

  const payloads = state?.payloads;
  const session = payloads?.session;
  const battle = payloads?.battle;
  const driver = payloads?.driver;
  const battlePair = pending.length ? pending : summary?.battle;
  const classes = summary?.classes || [];

  return (
    <div className="space-y-5">
      {error && <div className="px-4 py-3 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">{error}</div>}

      {summary?.mode === 'playback'
        ? <PlaybackTransport showId={showId} playback={summary.playback} control={timingBridgeAPI.controlPlayback} speeds={[0.5, 1, 2, 4, 8, 16]} />
        : <HealthLight health={health} />}

      <BroadcastLinks showId={showId} />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Metric
          label="Data age"
          value={upstream.ageSeconds == null ? '-' : `${upstream.ageSeconds}s`}
          tone={upstream.ageSeconds > 15 ? 'bad' : upstream.ageSeconds > 5 ? 'warn' : 'good'}
          hint="Since the timing site last answered"
        />
        <Metric label="Timing site p50 / p95" value={upstream.p50Ms == null ? '-' : `${upstream.p50Ms} / ${upstream.p95Ms} ms`} hint={summary?.provider || ''} />
        <Metric
          label="Consecutive failures"
          value={upstream.consecutiveFailures ?? 0}
          tone={upstream.consecutiveFailures > 0 ? 'bad' : 'muted'}
          hint={`Reading every ${upstream.pollIntervalMs ?? '-'} ms`}
        />
        <Metric
          label="Pushes / min"
          value={pushesPerMinute}
          tone={publishFailures.length ? 'bad' : summary?.frozen ? 'warn' : 'default'}
          hint={summary?.frozen ? 'Frozen: graphics holding' : publishFailures.length ? `${publishFailures.length} stream(s) failing` : `${streams.filter(s => s.configured).length} stream(s) configured`}
        />
      </div>

      {/* The session, and the two controls that affect everything at once. */}
      <section className="px-4 py-3 bg-[#111111] border border-[#2a2a2a] rounded">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-white font-medium">{session?.title || summary?.title || 'Waiting for the first session…'}</span>
              <FlagPill flag={session?.flag} />
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {session?.event}
              {session?.clockRemaining && ` · ${session.clockRemaining} remaining`}
              {!session?.clockRemaining && session?.clockElapsed && ` · ${session.clockElapsed}`}
              {session?.lapsText && ` · ${session.lapsText}`}
              {session && ` · ${session.carCount} cars`}
            </div>
            {session?.bestLap && (
              <div className="text-xs text-purple-400 mt-0.5">Fastest lap {session.bestLap.time}, {session.bestLap.short || session.bestLap.name}</div>
            )}
            {!state?.live && state?.note && <div className="text-xs text-amber-400 mt-1">{state.note} Graphics are holding the last session.</div>}
          </div>
          <button
            onClick={() => act('freeze', () => timingBridgeAPI.setFrozen(showId, !summary?.frozen))}
            disabled={busy === 'freeze'}
            className={`px-4 py-2 text-sm rounded border transition-colors ${
              summary?.frozen
                ? 'bg-cyan-900/40 border-cyan-600 text-cyan-200 hover:bg-cyan-900/60'
                : 'bg-[#0a0a0a] border-[#2a2a2a] text-gray-300 hover:border-[#3a3a3a]'
            }`}
            title="Hold every graphic on its current data. Timing keeps being read; unfreezing publishes the current picture at once."
          >
            {summary?.frozen ? 'Frozen: release' : 'Freeze graphics'}
          </button>
        </div>
      </section>

      {/* Tower class */}
      {classes.length > 1 && (
        <section>
          <h2 className="text-sm font-semibold text-white mb-2">Tower class</h2>
          <div className="flex flex-wrap gap-2">
            {[{ code: '', name: 'All', count: state?.order?.length ?? 0 }, ...classes].map(c => {
              const active = (summary?.config?.classFilter || '') === c.code;
              return (
                <button
                  key={c.code || 'all'}
                  disabled={busy === 'class'}
                  onClick={() => act('class', () => timingBridgeAPI.updateConfig(showId, { class_filter: c.code || null }))}
                  className={`px-3 py-1.5 text-xs rounded border transition-colors ${active ? 'bg-blue-600 border-blue-500 text-white' : 'bg-[#111111] border-[#2a2a2a] text-gray-300 hover:border-[#3a3a3a]'}`}
                >
                  {c.bg && <span className="inline-block w-2 h-2 rounded-sm mr-1.5 align-middle" style={{ background: c.bg }} />}
                  {c.name} <span className="opacity-50">{c.count}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Driver and battle */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <section className="px-4 py-3 bg-[#111111] border border-[#2a2a2a] rounded">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-white">Driver</h2>
            {driver?.selected && (
              <button onClick={() => act('driver', () => timingBridgeAPI.setDriver(showId, null))} className="text-[11px] text-gray-500 hover:text-gray-300">Clear</button>
            )}
          </div>
          {driver?.selected
            ? (
              <div className="flex items-center gap-4">
                <div className="text-2xl font-bold text-white tabular-nums w-10">{driver.pos}</div>
                <div className="min-w-0 text-xs">
                  <div className="text-sm text-white font-medium truncate">#{driver.no} {driver.name}</div>
                  <div className="text-gray-500">
                    {[driver.team, driver.cls].filter(Boolean).join(' · ')}
                    {driver.gap && ` · ${driver.gap}`}
                    {driver.bestLap && ` · best ${driver.bestLap}`}
                    {driver.gainedSinceStart != null && ` · ${driver.gainedSinceStart >= 0 ? '+' : ''}${driver.gainedSinceStart} since the start`}
                  </div>
                </div>
              </div>
            )
            : <p className="text-xs text-gray-500">Click a name in the running order to feature that car.</p>}
        </section>

        <section className="px-4 py-3 bg-[#111111] border border-[#2a2a2a] rounded">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-white">Battle</h2>
            <div className="flex gap-2">
              <button
                onClick={() => act('battle', () => timingBridgeAPI.autoBattle(showId))}
                disabled={busy === 'battle'}
                className={`text-[11px] px-2.5 py-1 rounded border transition-colors ${
                  summary?.battleMode === 'auto' ? 'bg-purple-950/50 border-purple-700 text-purple-300' : 'bg-[#0a0a0a] border-[#2a2a2a] text-gray-400 hover:border-[#3a3a3a]'
                }`}
                title="Follow the closest same-lap pair in the top ten"
              >
                {summary?.battleMode === 'auto' ? 'Auto on' : 'Auto'}
              </button>
              {battle?.active && (
                <button onClick={() => act('battle', () => timingBridgeAPI.setBattle(showId, null))} className="text-[11px] text-gray-500 hover:text-gray-300">Clear</button>
              )}
            </div>
          </div>
          {battle?.active
            ? (
              <div className="text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-white">P{battle.ahead.pos} {battle.ahead.short}</span>
                  <span className="text-lg font-bold text-white tabular-nums">{battle.gap || '-'}</span>
                  <span className="text-white">P{battle.behind.pos} {battle.behind.short}</span>
                </div>
                <div className={`mt-1 text-center ${battle.trend === 'closing' ? 'text-green-400' : battle.trend === 'pulling' ? 'text-red-400' : 'text-gray-500'}`}>
                  {battle.trend ? `${battle.trend} ${battle.trendPerLap}/lap` : 'Trend after the next laps'}
                </div>
              </div>
            )
            : <p className="text-xs text-gray-500">Tick two cars in the running order, or turn on Auto.</p>}
        </section>
      </div>

      <ChampionshipPanel showId={showId} summary={summary} payload={payloads?.championship} onChanged={onChanged} />
      <PhotosPanel showId={showId} photos={summary?.photos} onChanged={onChanged} />

      {/* The running order, which is also the control surface. */}
      {state?.order?.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-white mb-2">Running order</h2>
          <RunningOrder
            rows={state.order}
            driver={summary?.driver}
            battle={battlePair}
            busy={!!busy}
            onDriver={(no) => act('driver', () => timingBridgeAPI.setDriver(showId, no))}
            onBattle={toggleBattle}
          />
        </section>
      )}

      {/* Moments */}
      {state?.events?.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-white mb-2">Live moments</h2>
          <div className="space-y-1">
            {state.events.map(e => (
              <div key={e.id} className="flex items-center gap-2 px-3 py-1.5 bg-[#111111] border border-[#2a2a2a] rounded text-xs">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-400 w-24">{e.type}</span>
                <span className="text-gray-300">{e.text}</span>
                <span className="ml-auto text-gray-600">{Math.round(e.ttlMs / 1000)}s</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Move to another session or event without stopping. */}
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">Follow something else</h2>
        <div className="flex gap-2">
          <input
            value={switchTo}
            onChange={e => setSwitchTo(e.target.value)}
            placeholder={summary?.url || 'Another live timing URL'}
            className="flex-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500"
          />
          <button
            onClick={() => act('switch', () => timingBridgeAPI.updateConfig(showId, { input: switchTo.trim(), provider: 'auto' })).then(() => setSwitchTo(''))}
            disabled={!switchTo.trim() || busy === 'switch'}
            className="px-4 py-2 bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#3a3a3a] disabled:opacity-40 text-white text-sm rounded transition-colors"
          >
            {busy === 'switch' ? 'Switching…' : 'Switch'}
          </button>
        </div>
        <p className="text-[10px] text-gray-600 mt-1.5">
          Checked before anything changes: a wrong URL leaves live timing on what it is following now. Graphics keep the last
          picture until the new source delivers. Speedhive follows its event's next session by itself.
        </p>
      </section>

      {/* Streams: which token each one is actually writing to. */}
      {streams.some(s => s.configured) && (
        <section>
          <h2 className="text-sm font-semibold text-white mb-2">Streams</h2>
          <div className="space-y-1">
            {streams.filter(s => s.configured).map(s => (
              <div key={s.stream} className="flex items-center gap-3 px-3 py-2 bg-[#111111] border border-[#2a2a2a] rounded text-xs">
                <span className="text-gray-300 w-20 flex-shrink-0">{s.stream}</span>
                <code className="text-gray-500 flex-1 truncate" title="Compare with the token in Singular's Data Stream Manager">{s.tokenHint || '-'}</code>
                <span className={s.consecutiveFailures > 0 ? 'text-red-400' : 'text-gray-600'}>
                  {s.consecutiveFailures > 0 ? `${s.consecutiveFailures} failing` : `${s.pushesLastMinute || 0}/min`}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Exactly what each composition receives. */}
      <section>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500">Payload:</span>
          {STREAM_VIEWS.map(name => (
            <button
              key={name}
              onClick={() => setView(v => (v === name ? '' : name))}
              className={`text-xs px-2 py-0.5 rounded border ${view === name ? 'border-blue-500 text-white' : 'border-[#2a2a2a] text-gray-500 hover:text-gray-300'}`}
            >
              {name}
            </button>
          ))}
        </div>
        {view && (
          <pre className="mt-2 p-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded text-[10px] text-gray-400 overflow-auto max-h-96">
            {JSON.stringify(payloads?.[view] ?? { note: 'No session yet.' }, null, 2)}
          </pre>
        )}
      </section>
    </div>
  );
}
